import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'phosphor-react';
import { Navbar } from '@/components/landing/Navbar';
import { PerfectForSection } from '@/components/landing/PerfectForSection';
import { IntegrationsSection } from '@/components/landing/IntegrationsSection';
import { PrivacySection } from '@/components/landing/PrivacySection';
import Cta from '@/components/landing/Cta';
import { Footer } from '@/components/landing/Footer';
import { HeroTitle } from '@/components/landing/HeroTitle';
import { MockChatInput } from '@/components/landing/MockChatInput';
import { useEffect } from 'react';

export default function LandingPage() {
  const { t } = useLanguage();

  useEffect(() => {
    let loco: any | undefined;
    let destroyed = false;

    (async () => {
      const mod: any = await import('locomotive-scroll');
      const LocomotiveScroll = mod?.default ?? mod;
      if (destroyed) return;

      loco = new LocomotiveScroll({
        lenisOptions: {
          lerp: 0.08,
        },
      });
      try {
        loco?.start?.();
      } catch {}

      window.setTimeout(() => {
        try {
          loco?.resize?.();
        } catch {}
      }, 250);
    })();

    return () => {
      destroyed = true;
      try {
        loco?.destroy?.();
      } catch {}
    };
  }, []);

  return (
    <>
      <Navbar />
      <section className="relative flex flex-col overflow-hidden min-h-screen theme-bg">
        <div
          className="absolute inset-0 pointer-events-none opacity-95"
          style={{
            backgroundImage: `
              radial-gradient(900px 520px at 20% 0%,
                color-mix(in oklab, var(--nt-primary) 36%, transparent) 0%,
                transparent 72%
              ),
              radial-gradient(900px 520px at 80% 0%,
                color-mix(in oklab, var(--brand-400) 28%, transparent) 0%,
                transparent 72%
              ),
              radial-gradient(720px 360px at 50% 18%,
                color-mix(in oklab, var(--brand-400) 18%, transparent) 0%,
                transparent 72%
              ),
              linear-gradient(to bottom,
                var(--background) 0%,
                color-mix(in oklab, var(--background) 92%, transparent) 52%,
                transparent 100%
              )
            `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '160% 140%, 160% 140%, 140% 120%, 100% 100%',
            animation: 'paladino-gradient-pan 12s ease-in-out infinite',
          }}
        />

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            maskImage: 'radial-gradient(820px 520px at 50% 22%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 72%)',
            WebkitMaskImage: 'radial-gradient(820px 520px at 50% 22%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 72%)',
          }}
        >
          <div
            className="absolute left-0 top-0 w-full h-full"
            style={{
              background:
                'radial-gradient(520px 320px at 35% 18%, color-mix(in oklab, var(--nt-primary) 36%, transparent) 0%, transparent 70%)',
              filter: 'blur(54px)',
              animation: 'paladino-blob-float-a 7.5s ease-in-out infinite',
              willChange: 'transform, opacity',
            }}
          />
          <div
            className="absolute left-0 top-0 w-full h-full"
            style={{
              background:
                'radial-gradient(520px 320px at 65% 20%, color-mix(in oklab, var(--brand-400) 34%, transparent) 0%, transparent 70%)',
              filter: 'blur(58px)',
              animation: 'paladino-blob-float-b 8.5s ease-in-out infinite',
              willChange: 'transform, opacity',
            }}
          />
        </div>

        {/* Builder Canvas Grid Background */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(to right, color-mix(in oklab, var(--foreground) 8%, transparent) 1px, transparent 1px),
              linear-gradient(to bottom, color-mix(in oklab, var(--foreground) 8%, transparent) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 80%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 80%)',
          }}
        />

        {/* Main content */}
        <div className="relative z-10 flex flex-col items-center flex-1 justify-center pt-20 pb-16">
          {/* Hero text */}
          <div className="w-full flex flex-col items-center px-6">
            <div className="w-full max-w-[1400px] flex flex-col items-center">
              <div className="w-full max-w-6xl flex flex-col items-center">
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Link
                    to="/register"
                    className="mb-8 flex items-center gap-2 px-3 py-1.5 rounded-full border theme-border bg-[color:var(--card-bg)] transition-colors cursor-pointer group w-fit hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,var(--foreground)_12%)]"
                  >
                    <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold uppercase tracking-wider">
                      {t('hero.betaWithCredits')}
                    </span>
                    <span className="text-sm font-medium text-[color:var(--foreground)]">{t('hero.introducingMultiAgent')}</span>
                    <ArrowRight className="w-4 h-4 text-[color:var(--foreground-muted)] group-hover:text-[color:var(--foreground)] group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </motion.div>

                {/* Main Headline */}
                <HeroTitle
                  className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-light leading-[1.05] text-balance text-center tracking-tight pb-2 theme-text"
                  delay={0.2}
                />

                {/* Subheadline */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mt-6 text-lg md:text-xl text-center max-w-2xl leading-relaxed font-light text-[color:var(--foreground-muted)]"
                >
                  {t('hero.buildAgentsDesc')}
                </motion.p>

                {/* Dashboard Preview - Chat Input Mock */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="relative w-full mt-16 flex justify-center perspective-[1200px]"
                >
                  <div className="relative w-full max-w-4xl">
                    <MockChatInput />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PerfectForSection />

      <PrivacySection />

      <IntegrationsSection />

      <Cta />

      <Footer />
    </>
  );
}
